import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal';

/**
 * SessionExpiredModal Component
 *
 * Displays a modal when the user's session is invalidated (logged in from another device).
 * Listens for 'session-invalidated' custom event dispatched from api.js interceptor.
 * Automatically redirects to login page when user acknowledges the message.
 *
 * Uses z-index 9999 to ensure it appears above all other content, including PDF viewers.
 */
const SessionExpiredModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionInvalidated = (event) => {
      // Only open if not already open (prevent duplicates)
      if (!isOpen) {
        setMessage(event.detail.message);
        setIsOpen(true);
      }
    };

    // Listen for the custom event
    window.addEventListener('session-invalidated', handleSessionInvalidated);

    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener('session-invalidated', handleSessionInvalidated);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    // Redirect to login page
    navigate('/login');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Session Expired"
      size="medium"
      zIndex="z-[9999]"
    >
      <div className="p-6 text-center">
        {/* Warning Icon */}
        <div className="mb-4 flex justify-center">
          <div className="rounded-full bg-yellow-100 p-3">
            <svg
              className="h-12 w-12 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Message */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Session Expired
        </h3>
        <p className="text-gray-600 mb-2">
          {message}
        </p>
        <p className="text-gray-700 font-medium mb-6">
          Please login to continue using the service.
        </p>

        {/* Action Button */}
        <button
          onClick={handleClose}
          className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Login to Continue
        </button>
      </div>
    </Modal>
  );
};

export default SessionExpiredModal;
